import { Component, Inject, OnInit } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog } from '@angular/material/dialog';
import { MTX_DRAWER_DATA, MtxDrawerRef } from '@ng-matero/extensions/drawer';
import Decimal from 'decimal.js';
import { AdjustmentsService } from '@app/shared/services/adjustments.service';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { ModalConfirmationComponent } from '@app/shared/components/modal-confirmation/modal-confirmation.component';
import { ShipmentChargeStatus } from '@app/shared/constants';
import { HelpersService } from '@app/shared/services/helpers.service';

export type RemoveSurchargesDrawerData =
  | { mode: 'single'; companyAdjustment: any; shipment: any }
  | { mode: 'bulk'; companyAdjustment: any };

export interface SurchargeLine {
  code: string;
  name: string;
  amount: number;
  /** Which details bucket the line lives in — sent back so the backend strips the right field. */
  source: 'surcharge' | 'option';
}

export interface ShipmentView {
  shipmentId: number;
  trackingNumber: string;
  providerName: string;
  currentAdjustmentTotal: number;
  surchargeLines: SurchargeLine[];
}

@Component({
  standalone: false,
  selector: 'app-remove-surcharges-drawer',
  templateUrl: './remove-surcharges-drawer.component.html',
  styleUrls: ['./remove-surcharges-drawer.component.scss'],
})
export class RemoveSurchargesDrawerComponent implements OnInit {
  public mode: 'single' | 'bulk';
  public companyName: string;
  public shipmentsView: ShipmentView[] = [];
  /** Composite key: "shipmentId|code|amount" */
  public selection = new SelectionModel<string>(true, []);
  public runningRemovalTotal = 0;
  public saving = false;

  constructor(
    public drawerRef: MtxDrawerRef<RemoveSurchargesDrawerComponent>,
    @Inject(MTX_DRAWER_DATA) public data: RemoveSurchargesDrawerData,
    private adjustmentsService: AdjustmentsService,
    private bannerNotificationsService: BannerNotificationsService,
    private dialog: MatDialog,
    private helpersService: HelpersService
  ) {}

  ngOnInit(): void {
    this.mode = this.data.mode;
    this.companyName = this.data.companyAdjustment?.name || this.data.companyAdjustment?.account?.email || '';

    const shipmentsRaw: any[] =
      this.data.mode === 'single'
        ? [this.data.shipment]
        : (this.data.companyAdjustment.shipments || []);

    this.shipmentsView = this._buildShipmentsView(shipmentsRaw);
  }

  private _buildShipmentsView(shipments: any[]): ShipmentView[] {
    const views: ShipmentView[] = [];

    for (const shipment of shipments) {
      // Pending AND previously-declined adjustments are both removable (AP + AD), matching the
      // backend WHERE clause and the legacy remove-Other flow.
      const removableStatuses = [ShipmentChargeStatus.ADJUSTMENT_PENDING, ShipmentChargeStatus.ADJUSTMENT_DECLINED];
      const apCharges = (shipment.rates?.adjustmentCharges || []).filter((c: any) =>
        removableStatuses.includes(c.status)
      );

      const surchargeLines: SurchargeLine[] = [];
      for (const charge of apCharges) {
        const rawSurcharges: any[] = Array.isArray(charge.surcharges) ? charge.surcharges : [];
        for (const line of rawSurcharges) {
          if (line && line.code != null && line.amount != null) {
            surchargeLines.push({
              code: line.code,
              name: line.name || line.code,
              amount: line.amount,
              source: line.source === 'option' ? 'option' : 'surcharge',
            });
          }
        }
      }

      if (surchargeLines.length === 0) {
        continue;
      }

      // Sum adjustment total across all AP charges
      const adjustmentTotal = apCharges.reduce(
        (acc: number, c: any) => new Decimal(acc).plus(c.amount || 0).toNumber(),
        0
      );

      // Friendly provider name from the ShippingProviders enum (e.g. "ups" → "UPS"),
      // falling back to whatever raw value is present.
      const providerName =
        (shipment.provider && this.helpersService.convertShipmentProviderToString(shipment.provider)) ||
        shipment.providerName ||
        shipment.provider ||
        '';

      views.push({
        shipmentId: shipment.id,
        trackingNumber: shipment.tracking || shipment.trackingNumber || String(shipment.id),
        providerName,
        currentAdjustmentTotal: adjustmentTotal,
        surchargeLines,
      });
    }

    return views;
  }

  public key(shipmentId: number, line: SurchargeLine): string {
    // Format: "shipmentId|source|code|amount". Source precedes code so the backend can route the
    // removal to the right details bucket even when a surcharge and an option share {code, amount}.
    return `${shipmentId}|${line.source}|${line.code}|${line.amount}`;
  }

  public toggle(shipmentId: number, line: SurchargeLine): void {
    const k = this.key(shipmentId, line);
    this.selection.toggle(k);
    this._recalculateTotal();
  }

  private _recalculateTotal(): void {
    let total = new Decimal(0);
    for (const k of this.selection.selected) {
      const parts = k.split('|');
      const amount = parseFloat(parts[parts.length - 1]);
      if (!isNaN(amount)) {
        total = total.plus(amount);
      }
    }
    this.runningRemovalTotal = total.toDecimalPlaces(2).toNumber();
  }

  public cancel(): void {
    this.drawerRef.dismiss(null);
  }

  public save(): void {
    if (this.selection.isEmpty() || this.saving) {
      return;
    }

    const payloads = this._buildPayloads();
    const companyId: number = this.data.companyAdjustment.account.companyId;

    // Detect "all surcharges of at least one shipment are selected"
    const shipmentsWithAllSelected = this.shipmentsView.filter((sv) =>
      sv.surchargeLines.every((line) => this.selection.isSelected(this.key(sv.shipmentId, line)))
    );
    const hasAllSelectedForAnyShipment = shipmentsWithAllSelected.length > 0;

    const modalData = hasAllSelectedForAnyShipment
      ? {
          title: `Remove ALL surcharges from ${shipmentsWithAllSelected.length} shipment(s)?`,
          content: `This will remove all surcharge lines from: ${shipmentsWithAllSelected.map((s) => s.trackingNumber).join(', ')}. The adjustment will remain pending.`,
          btnColor: 'warn',
          confirmBtnLabel: 'Remove',
        }
      : {
          title: 'Remove surcharges and recalculate?',
          content: `Remove ${this.selection.selected.length} selected surcharge line(s) totalling ${new Decimal(this.runningRemovalTotal).toFixed(2)} CAD? Taxes will be recalculated server-side.`,
          btnColor: 'primary',
          confirmBtnLabel: 'Remove and recalculate',
        };

    const dialogRef = this.dialog.open(ModalConfirmationComponent, {
      width: '480px',
      data: modalData,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.saving = true;
      this.adjustmentsService
        .removeSurchargesAndRecalculate({ companyId, payloads })
        .subscribe(
          () => {
            this.saving = false;
            this.drawerRef.dismiss({ refreshNeeded: true });
          },
          (err: any) => {
            this.saving = false;
            const msg = err?.error?.message || err?.message || 'Failed to remove surcharges';
            this.bannerNotificationsService.error(msg);
          }
        );
    });
  }

  private _buildPayloads(): {
    shipmentId: number;
    surchargeKeys: { code: string; amount: number; source: 'surcharge' | 'option' }[];
  }[] {
    const grouped = new Map<number, { code: string; amount: number; source: 'surcharge' | 'option' }[]>();

    for (const k of this.selection.selected) {
      const parts = k.split('|');
      // key format: "shipmentId|source|code|amount". shipmentId is first, source second, amount last;
      // anything between (rare, if a code itself contained '|') re-joins as the code.
      const shipmentId = parseInt(parts[0], 10);
      const source = parts[1] === 'option' ? 'option' : 'surcharge';
      const amount = parseFloat(parts[parts.length - 1]);
      const code = parts.slice(2, parts.length - 1).join('|');

      if (!grouped.has(shipmentId)) {
        grouped.set(shipmentId, []);
      }
      grouped.get(shipmentId)!.push({ code, amount, source });
    }

    return Array.from(grouped.entries()).map(([shipmentId, surchargeKeys]) => ({
      shipmentId,
      surchargeKeys,
    }));
  }
}
